import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { z } from 'zod';

import { useFeatureFlags } from '@/components/providers/FeatureFlagsProvider';
import { useAwsSyncNotification } from '@/hooks/use-aws-sync-notification';
import { useGcpSyncNotification } from '@/hooks/use-gcp-sync-notification';
import { useScanProgress } from '@/hooks/use-scan-progress';
import { gcpServiceAccountQueries } from '@/lib/queries/gcpServiceAccount';
import { awsCredentialsQueries } from '@/lib/queries/integrations/cloud/aws/credentials';
import { awsRoleQueries } from '@/lib/queries/integrations/cloud/aws/roles';
import { k8sClustersQueries } from '@/lib/queries/integrations/containers/k8sClusters';
import { incidentsManagementPagerDutyTokensQueries } from '@/lib/queries/integrations/incidentsManagement/pagerduty/tokens';
import { slackQueries } from '@/lib/queries/integrations/messaging/slack';
import { monitoringDatadogQueries } from '@/lib/queries/integrations/monitoring/datadog';
import { monitoringGrafanaQueries } from '@/lib/queries/integrations/monitoring/grafana';
import { githubConfigQueries } from '@/lib/queries/integrations/scm/github';
import { gitlabConfigQueries } from '@/lib/queries/integrations/scm/gitlab';
import { terraformBackendBucketsQueries } from '@/lib/queries/integrations/terraformBackend/buckets';
import { terraformBackendHcpCloudQueries } from '@/lib/queries/integrations/terraformBackend/hcpCloud';
import { preLoadDefaultProject } from '@/lib/queries/project';

import { Integrations } from './-components/Integrations';
import { TryIt } from './-components/TryIt';
import { getIntegrationData } from './-data';

export const Route = createFileRoute('/_root/integrations/')({
  component: RouteComponent,
  validateSearch: (s: Record<string, unknown>) =>
    z
      .object({
        github: z.string().optional(),
        gitlab: z.string().optional(),
      })
      .parse(s),
  beforeLoad: ({ search }) => {
    // Allow access to integrations from OAuth callbacks regardless of getting-started status
    if (search.github != null || search.gitlab != null) {
      return {};
    }
  },
  loader: async ({ context }) => {
    const defaultProject = await preLoadDefaultProject({ context });
    context.queryClient.prefetchQuery(
      githubConfigQueries.list(defaultProject.id),
    );
    context.queryClient.prefetchQuery(
      gitlabConfigQueries.list(defaultProject.id),
    );
    context.queryClient.prefetchQuery(
      awsCredentialsQueries.list(defaultProject.id),
    );
    context.queryClient.prefetchQuery(awsRoleQueries.list(defaultProject.id));
    context.queryClient.prefetchQuery(
      gcpServiceAccountQueries.list(defaultProject.id),
    );

    return defaultProject;
  },
});

function RouteComponent() {
  const defaultProject = Route.useLoaderData();
  const {
    FeatureDatadogIntegration,
    FeatureGrafanaIntegration,
    FeatureGCPIntegration,
    FeaturePagerduty,
    FeatureGitlabSaaS,
  } = useFeatureFlags();

  const { data: githubInstall } = useQuery(
    githubConfigQueries.list(defaultProject.id),
  );

  const { data: gitlabInstall } = useQuery(
    gitlabConfigQueries.list(defaultProject.id),
  );

  const { data: awsUsers } = useQuery(
    awsCredentialsQueries.list(defaultProject.id),
  );

  const { data: awsRoles } = useQuery(awsRoleQueries.list(defaultProject.id));

  const { data: gcpServiceAccounts } = useQuery(
    gcpServiceAccountQueries.list(defaultProject.id),
  );

  const { data: buckets } = useQuery(
    terraformBackendBucketsQueries.list(defaultProject.id),
  );

  const { data: hcpCloudTokens } = useQuery(
    terraformBackendHcpCloudQueries.list(defaultProject.id),
  );

  const { data: datadogApiCredentials } = useQuery(
    monitoringDatadogQueries.list(defaultProject.id),
  );

  const { data: grafanaConfigurations } = useQuery(
    monitoringGrafanaQueries.list(defaultProject.id),
  );
  const { data: k8sClusters } = useQuery(
    k8sClustersQueries.list(defaultProject.id),
  );

  const scanProgressState = useScanProgress(defaultProject.id);

  // Monitor AWS credential status changes
  useAwsSyncNotification({ awsUsers, awsRoles });

  // Monitor GCP service account status changes
  useGcpSyncNotification({ gcpServiceAccounts });
  const { data: pagerDutyTokens } = useQuery(
    incidentsManagementPagerDutyTokensQueries.list(defaultProject.id),
  );

  const { data: slackChannels } = useQuery(
    slackQueries.listChannels(defaultProject.id),
  );

  const integrationsData = useMemo(
    () =>
      getIntegrationData({
        awsRoles,
        awsUsers,
        gcpServiceAccounts,
        githubInstall,
        gitlabInstall,
        gitlabEnabled: FeatureGitlabSaaS !== false, // Default to enabled if flag not configured
        // buckets,
        hcpCloudTokens,
        datadogApiCredentials,
        datadogEnabled: FeatureDatadogIntegration === true,
        grafanaConfigurations,
        grafanaEnabled: FeatureGrafanaIntegration === true,
        gcpEnabled: FeatureGCPIntegration === true,
        k8sClusters,
        awsScanProgress: scanProgressState.aws,
        gcpScanProgress: scanProgressState.gcp,
        pagerDutyTokens,
        pagerdutyEnabled: FeaturePagerduty === true,
        slackConnected: slackChannels?.connectedState === true,
      }),
    [
      awsRoles,
      awsUsers,
      gcpServiceAccounts,
      githubInstall,
      gitlabInstall,
      FeatureGitlabSaaS,
      // buckets,
      hcpCloudTokens,
      datadogApiCredentials,
      FeatureDatadogIntegration,
      grafanaConfigurations,
      FeatureGrafanaIntegration,
      FeatureGCPIntegration,
      k8sClusters,
      scanProgressState,
      pagerDutyTokens,
      FeaturePagerduty,
      slackChannels?.connectedState,
    ],
  );

  return (
    <>
      <Integrations
        data={integrationsData}
        defaultProjectId={defaultProject.id}
      />
      <TryIt
        awsUsers={awsUsers}
        awsRoles={awsRoles}
        githubInstall={githubInstall}
        buckets={buckets}
        k8sClusters={k8sClusters}
      />
    </>
  );
}
